/**
 * Sprite loading for the dinosaurs. Each tier has two PNGs: the full-size
 * showcase art in public/art/dinos/<id>.png (the shop) and a half-size copy
 * in public/art/yard/<id>.png (the herd on the canvas, where 64 px would be
 * as tall as the egg). Until a PNG lands, the yard and shop fall back to the
 * coloured placeholder, so tiers can get real art one at a time.
 *
 * Loading is fire-and-forget: the render loop asks for a sprite every frame
 * and gets null until the image has decoded, then the element from then on.
 */

import { DINOS } from '../data/dinos';
import { resampleRGBA } from '../game/resample';

export type SpriteKind = 'full' | 'yard';

/** A decoded PNG, or a canvas built from one: anything drawImage takes. */
export type SpriteImage = HTMLImageElement | HTMLCanvasElement;

/**
 * Stand-in art for the era residents and their evolved forms until their own
 * is drawn (docs/era-residents-plan.md, R2): a similar species' sprites,
 * walk, skeleton and icon. Delete an entry when its art lands.
 */
const STAND_INS: Readonly<Record<string, string>> = {
  shuvuuia: 'compsognathus',
  mononykus: 'sinosauropteryx',
  pyroraptor: 'oviraptor',
  balaur: 'gigantoraptor',
  allosaurus: 'tyrannosaurus',
  saurophaganax: 'tyrannosaurus-alpha',
  moa: 'gallimimus',
  'elephant-bird': 'ornithomimus',
};

/**
 * Skeletons borrowed by species with their own sprites but no mount of their
 * own yet: the tier-15 pair (2026-09-29), the nearest relatives' bones.
 * Checked before STAND_INS.
 */
const MOUNT_STAND_INS: Readonly<Record<string, string>> = {
  baryonyx: 'spinosaurus',
  suchomimus: 'ichthyovenator',
};

/** The file name a species' art is under: its own, or its stand-in's. */
export function artIdFor(id: string): string {
  return STAND_INS[id] ?? id;
}

export function dinoSpriteUrl(id: string, kind: SpriteKind): string {
  const dir = kind === 'yard' ? 'yard' : 'dinos';
  return `${import.meta.env.BASE_URL}art/${dir}/${artIdFor(id)}.png`;
}

/**
 * The skeletal mount for a sprite id — what the Museum displays once that
 * dinosaur has been excavated. One per tier and one per evolved form, named
 * for the sprite they belong to, so `spriteIdFor` picks the right bones.
 */
export function mountUrl(spriteId: string): string {
  return `${import.meta.env.BASE_URL}art/mounts/${MOUNT_STAND_INS[spriteId] ?? artIdFor(spriteId)}.png`;
}

const loaded = new Map<string, HTMLImageElement>();
const pending = new Set<string>();
const missing = new Set<string>();

/** The decoded sprite for a tier, or null if it is still loading or does not exist. */
export function dinoSprite(id: string, kind: SpriteKind): HTMLImageElement | null {
  const key = `${kind}:${id}`;
  const img = loaded.get(key);
  if (img) return img;
  if (missing.has(key) || pending.has(key)) return null;

  pending.add(key);
  const el = new Image();
  el.addEventListener('load', () => {
    pending.delete(key);
    loaded.set(key, el);
  });
  el.addEventListener('error', () => {
    pending.delete(key);
    missing.add(key);
  });
  el.src = dinoSpriteUrl(id, kind);
  return null;
}

/** First and last rows of a decoded sprite that hold any opaque pixel. */
export function opaqueRows(img: HTMLImageElement): { top: number; bottom: number } {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { top: 0, bottom: img.naturalHeight - 1 };
  ctx.drawImage(img, 0, 0);
  const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
  let top = 0;
  let bottom = height - 1;
  const rowHasInk = (y: number): boolean => {
    for (let x = 0; x < width; x++) if (data[(y * width + x) * 4 + 3]! > 0) return true;
    return false;
  };
  while (top < bottom && !rowHasInk(top)) top++;
  while (bottom > top && !rowHasInk(bottom)) bottom--;
  return { top, bottom };
}

/**
 * The stream along the bottom of the yard, one strip per era's painting
 * (public/art/water/), tiled across the width; the Giant era's is a row of
 * animation frames (ANIMATED_WATER in nest.ts). Null until loaded.
 */
const waters = new Map<string, HTMLImageElement | null>();
export function waterImage(name: BackgroundName): HTMLImageElement | null {
  const hit = waters.get(name);
  if (hit !== undefined) return hit;
  waters.set(name, null);
  const el = new Image();
  el.addEventListener('load', () => waters.set(name, el));
  el.src = `${import.meta.env.BASE_URL}art/water/${name}.png`;
  return null;
}

/** The box round a sprite's opaque pixels; built once per image. */
const inkBoxes = new WeakMap<
  SpriteImage,
  { top: number; bottom: number; left: number; right: number }
>();
export function inkBoundsOf(img: SpriteImage): {
  top: number;
  bottom: number;
  left: number;
  right: number;
} {
  const hit = inkBoxes.get(img);
  if (hit) return hit;
  const w = img.width;
  const h = img.height;
  const data = pixelsOf(img, 0, 0, w, h);
  let top = h;
  let bottom = -1;
  let left = w;
  let right = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if ((data[(y * w + x) * 4 + 3] ?? 0) > 0) {
        top = Math.min(top, y);
        bottom = Math.max(bottom, y);
        left = Math.min(left, x);
        right = Math.max(right, x);
      }
    }
  }
  const box =
    bottom < 0 ? { top: 0, bottom: h - 1, left: 0, right: w - 1 } : { top, bottom, left, right };
  inkBoxes.set(img, box);
  return box;
}

/** Kick off every tier's load up front so nothing pops in on first purchase. */
export function preloadDinoSprites(): void {
  for (const def of DINOS) {
    dinoSprite(def.id, 'full');
    dinoSprite(def.id, 'yard');
  }
}

// ---------------------------------------------------------------------------
// The scene background.

/**
 * The scene backgrounds, one per look. An era can call for its own painting
 * (data/eras.ts), and each is a whole scene (224 x 232, or a little less) rather than a change to
 * the default one, so a new one is a file and a line rather than a diff. The default is a 768 px
 * PixelLab painting in its own colours (docs/art-prompts.md; exempt from the palette check); any
 * width works, since the yard centres the painting and mirror-tiles only what it does not reach.
 */
export const BACKGROUNDS = {
  default: 'background.png',
  volcanic: 'background-volcanic.png',
  jurassic: 'background-jurassic.png',
  cretaceous: 'background-cretaceous.png',
  'ice-age': 'background-ice-age.png',
  night: 'background-night.png',
  giant: 'background-giant.png',
} as const;

export type BackgroundName = keyof typeof BACKGROUNDS;

/**
 * Paintings whose extra sky (the rows above them on a tall canvas) gets a few
 * specks: stars at night and dusk, snow in the Ice Age. A daylight sky with
 * stars in it reads as a mistake, so the rest get plain sky.
 */
export const SPECKLED_SKIES: ReadonlySet<BackgroundName> = new Set<BackgroundName>([
  'default',
  'volcanic',
  'night',
  'ice-age',
]);

interface LoadedBackground {
  image: HTMLImageElement;
  /** The commonest colour of its top rows, which fills any rows above the painting. */
  sky: string;
  /** The commonest colour of its bottom rows, which fills any rows below it. */
  ground: string;
}

const loadedBackgrounds = new Map<BackgroundName, LoadedBackground>();
const requestedBackgrounds = new Set<BackgroundName>();

/**
 * A background painting once decoded, or null while it loads. It is drawn
 * bottom-aligned and mirror-tiled sideways, so it can be any size up to the
 * canvas height; the rows it does not reach take its sky and ground colours.
 */
export function backgroundImage(name: BackgroundName = 'default'): HTMLImageElement | null {
  const loaded = loadedBackgrounds.get(name);
  if (loaded) return loaded.image;
  if (requestedBackgrounds.has(name)) return null;
  requestedBackgrounds.add(name);
  const el = new Image();
  el.addEventListener('load', () => {
    const canvas = document.createElement('canvas');
    canvas.width = el.width;
    canvas.height = el.height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(el, 0, 0);
    // A corner pixel is not a safe sample: a generated painting can have a
    // stray colour there (a teal fleck under the Night sky's dirt), and it
    // then fills a whole band of the yard. The commonest colour of the edge
    // rows is what the eye reads as the sky or the ground.
    loadedBackgrounds.set(name, {
      image: el,
      sky: commonestColour(ctx, 0, Math.min(4, el.height), el.width),
      ground: commonestColour(ctx, Math.max(0, el.height - 4), el.height, el.width),
    });
  });
  el.src = `${import.meta.env.BASE_URL}art/${BACKGROUNDS[name]}`;
  return null;
}

/** The commonest opaque colour in rows [from, to) of a canvas, as an rgb() string. */
function commonestColour(
  ctx: CanvasRenderingContext2D,
  from: number,
  to: number,
  width: number,
): string {
  const { data } = ctx.getImageData(0, from, width, to - from);
  const counts = new Map<number, number>();
  let best = 0;
  let most = -1;
  for (let k = 0; k < data.length; k += 4) {
    const key = ((data[k] ?? 0) << 16) | ((data[k + 1] ?? 0) << 8) | (data[k + 2] ?? 0);
    const n = (counts.get(key) ?? 0) + 1;
    counts.set(key, n);
    if (n > most) {
      most = n;
      best = key;
    }
  }
  return `rgb(${best >> 16},${(best >> 8) & 255},${best & 255})`;
}

/** The colour filling the rows above a painting, once it has loaded. */
export function backgroundSkyColor(name: BackgroundName = 'default'): string | null {
  return loadedBackgrounds.get(name)?.sky ?? null;
}

/** The colour filling the rows below it. */
export function backgroundGroundColor(name: BackgroundName = 'default'): string | null {
  return loadedBackgrounds.get(name)?.ground ?? null;
}

// The same painting split into parallax layers (scripts/split-background.mjs):
// each is the full 224 x 232 canvas, so they composite at one origin in this
// order. Behind a nearer layer the one below is filled in, so a few pixels of
// relative shift show plausible content rather than holes.
export const BACKGROUND_LAYERS = ['sky', 'far', 'near', 'ground', 'trees'] as const;
export type BackgroundLayer = (typeof BACKGROUND_LAYERS)[number];

const layers = new Map<BackgroundLayer, HTMLImageElement>();
let layersRequested = false;

/** All five layers once every one has loaded, or null (the flat painting stands in). */
export function backgroundLayers(): ReadonlyMap<BackgroundLayer, HTMLImageElement> | null {
  if (!layersRequested) {
    layersRequested = true;
    for (const name of BACKGROUND_LAYERS) {
      const el = new Image();
      el.addEventListener('load', () => layers.set(name, el));
      el.src = `${import.meta.env.BASE_URL}art/bg/${name}.png`;
    }
  }
  return layers.size === BACKGROUND_LAYERS.length ? layers : null;
}

// ---------------------------------------------------------------------------
// Scene props: the egg, the nest, the falling gold egg, and the hatch strip
// (eight 96px frames; the egg's 64px window sits at 16,20 in every frame:
// cracks in frames 0-1, then the shell splitting open in 2-7).

export type SceneProp = 'egg' | 'nest' | 'egg-gold' | 'egg-hatch';

const props = new Map<SceneProp, HTMLImageElement>();
const propsRequested = new Set<SceneProp>();

/** The decoded prop from public/art/<name>.png, or null until it loads. */
export function sceneProp(name: SceneProp): HTMLImageElement | null {
  const img = props.get(name);
  if (img || propsRequested.has(name)) return img ?? null;
  propsRequested.add(name);
  const el = new Image();
  el.addEventListener('load', () => props.set(name, el));
  el.src = `${import.meta.env.BASE_URL}art/${name}.png`;
  return null;
}

// ---------------------------------------------------------------------------
// Meteors: public/art/meteors/<name>.png — a rock per kind (pebble 16,
// stone 20, iron 24, core 28) and the 24px fireball.

const meteorArt = new Map<string, HTMLImageElement>();
const meteorArtRequested = new Set<string>();

/** The decoded meteor image, or null until it loads. */
export function meteorSprite(name: string): HTMLImageElement | null {
  const img = meteorArt.get(name);
  if (img || meteorArtRequested.has(name)) return img ?? null;
  meteorArtRequested.add(name);
  const el = new Image();
  el.addEventListener('load', () => meteorArt.set(name, el));
  el.src = `${import.meta.env.BASE_URL}art/meteors/${name}.png`;
  return null;
}

// ---------------------------------------------------------------------------
// Walk cycles. A tier with public/art/walk/<id>.png gets real walking frames
// in the yard: a horizontal strip of square frames, each at least as big as
// the 64px art with the art's own window centred at the frame's bottom (a
// walk swings limbs and tails past the standing pose, so frames get room),
// plus a halved copy (<id>-32.png) for the yard's small step. The frame size
// is the strip's height.

/**
 * Flyers whose strip is whole wingbeats rather than one stroke, so it loops
 * forward like a walk; every other flyer's strip plays ping-pong.
 */
export const LOOPING_FLAPS: ReadonlySet<string> = new Set(['microraptor', 'anchiornis']);

export interface WalkSheet {
  image: SpriteImage;
  /** Frame size in the image's own pixels (frames are square). */
  frame: number;
  frames: number;
  /**
   * Image pixels per native pixel, when the strip was built on the backing
   * store's finer grid (sizedWalkSheet); a frame is drawn `frame / density`
   * native pixels square. Absent means 1.
   */
  density?: number;
}

const walks = new Map<string, WalkSheet>();
const walksMissing = new Set<string>();
const walksPending = new Set<string>();

/** The tier's walk sheet at a size, or null if it is still loading or does not exist. */
export function walkSheet(id: string, kind: SpriteKind): WalkSheet | null {
  const key = `${kind}:${id}`;
  const sheet = walks.get(key);
  if (sheet) return sheet;
  if (walksMissing.has(key) || walksPending.has(key)) return null;
  walksPending.add(key);
  const el = new Image();
  el.addEventListener('load', () => {
    walksPending.delete(key);
    const frame = el.naturalHeight;
    walks.set(key, { image: el, frame, frames: Math.max(1, Math.floor(el.naturalWidth / frame)) });
  });
  el.addEventListener('error', () => {
    walksPending.delete(key);
    walksMissing.add(key);
  });
  el.src = `${import.meta.env.BASE_URL}art/walk/${artIdFor(id)}${kind === 'yard' ? '-32' : ''}.png`;
  return null;
}

// ---- sizes between the two PNGs ----------------------------------------------

/** RGBA of one square window of an image. */
function pixelsOf(
  img: SpriteImage,
  sx: number,
  sy: number,
  w: number,
  h: number,
): Uint8ClampedArray {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  // Made only to be read back, as are opaqueRows' and a background's: kept
  // in main memory, the read skips a round trip through the GPU, which on
  // start-up (every shop icon is measured) cost a slow phone ~150 ms.
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return new Uint8ClampedArray(w * h * 4);
  ctx.drawImage(img, sx, sy, w, h, 0, 0, w, h);
  return ctx.getImageData(0, 0, w, h).data;
}

function canvasOf(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

const sized = new Map<string, SpriteImage>();

/**
 * How many image pixels a sprite drawn `size` native pixels square is built
 * with. Below the art's own size it is resampled onto the backing store's
 * finer grid (`render` pixels per native pixel) rather than the scene's, so a
 * small dinosaur keeps its horns and eye instead of shrinking to a blob
 * (docs/mockups/yard-resize-small.html). At a 2x backing store a dinosaur at
 * half the art's size is the art itself, pixel for pixel. At or above the
 * art's size, and on a 1x backing store, it stays on the native grid.
 */
function imagePixelsFor(size: number, full: number, render: number): number {
  return size < full && render > 1 ? Math.round(size * render) : size;
}

/**
 * A tier's art drawn at `size` native pixels square: an image `size` pixels
 * square, or finer below the art's size (imagePixelsFor), to be drawn into
 * `size` native pixels either way. Sizes that have PNGs use them; every other
 * is the full art resampled (game/resample.ts), built the first time it is
 * asked for and kept. Null while the full art is still loading.
 */
export function sizedDinoSprite(
  id: string,
  /** Native size of its full art (artSizeOf in data/dinos.ts). */
  full: number,
  size: number,
  render = 1,
): SpriteImage | null {
  const px = imagePixelsFor(size, full, render);
  if (px === full) return dinoSprite(id, 'full');
  if (px === full / 2) {
    const yard = dinoSprite(id, 'yard');
    if (yard) return yard;
  }
  const key = `${id}@${px}`;
  const hit = sized.get(key);
  if (hit) return hit;
  const src = dinoSprite(id, 'full');
  if (!src) return null;
  const w = src.naturalWidth;
  const h = src.naturalHeight;
  const canvas = canvasOf(px, px);
  const data = resampleRGBA(pixelsOf(src, 0, 0, w, h), w, h, px, px);
  canvas.getContext('2d')?.putImageData(new ImageData(data, px, px), 0, 0);
  sized.set(key, canvas);
  return canvas;
}

const sizedWalks = new Map<string, WalkSheet>();

/**
 * A walk strip for the art at `size`. A frame holds the sprite's window
 * centred at its bottom with a margin round it for the stride; the margin is
 * scaled with the art and rounded to whole pixels, so the window stays
 * centred and `(frame - size) / 2` still finds it. Built frame by frame from
 * the full strip. Below the art's size it is built on the finer grid, like
 * sizedDinoSprite, and says so in its `density`.
 */
export function sizedWalkSheet(
  id: string,
  /** Native size of its full art (artSizeOf in data/dinos.ts). */
  full: number,
  size: number,
  render = 1,
): WalkSheet | null {
  const px = imagePixelsFor(size, full, render);
  const density = px / size;
  const key = `${id}@${size}:${px}`;
  const hit = sizedWalks.get(key);
  if (hit) return hit;
  // The two sizes with PNGs, as sizedDinoSprite, so a dinosaur looks the
  // same standing and walking.
  const png = px === full ? walkSheet(id, 'full') : px === full / 2 ? walkSheet(id, 'yard') : null;
  if (png) {
    if (density === 1) return png;
    const sheet = { ...png, density };
    sizedWalks.set(key, sheet);
    return sheet;
  }
  if (px === full) return null;
  const src = walkSheet(id, 'full');
  if (!src) return null;
  const margin = Math.round((((src.frame - full) / 2) * px) / full);
  const frame = px + 2 * margin;
  const canvas = canvasOf(frame * src.frames, frame);
  const ctx = canvas.getContext('2d');
  for (let i = 0; i < src.frames; i++) {
    const data = resampleRGBA(
      pixelsOf(src.image, i * src.frame, 0, src.frame, src.frame),
      src.frame,
      src.frame,
      frame,
      frame,
    );
    ctx?.putImageData(new ImageData(data, frame, frame), i * frame, 0);
  }
  const sheet = { image: canvas, frame, frames: src.frames, density };
  sizedWalks.set(key, sheet);
  return sheet;
}

// ---- silhouettes ------------------------------------------------------------

const silhouettes = new WeakMap<SpriteImage, Map<string, HTMLCanvasElement>>();

/**
 * An image with every opaque pixel painted `color` and the rest left clear,
 * the same size as the image: draw it exactly where the image would go. The
 * evolution's flash is the dinosaur's own shape in white, not its box. Works
 * on a walk strip frame by frame, like outlineOf. Built once per image and
 * colour.
 */
export function silhouetteOf(img: SpriteImage, color: string): HTMLCanvasElement {
  let byColor = silhouettes.get(img);
  const hit = byColor?.get(color);
  if (hit) return hit;
  const canvas = canvasOf(img.width, img.height);
  const ctx = canvas.getContext('2d');
  if (ctx) {
    // Keep the image's shape, replace its colours: draw it, then fill over
    // only where it already has pixels.
    ctx.drawImage(img, 0, 0);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = color;
    ctx.fillRect(0, 0, img.width, img.height);
  }
  if (!byColor) {
    byColor = new Map();
    silhouettes.set(img, byColor);
  }
  byColor.set(color, canvas);
  return canvas;
}

// ---- outlines ---------------------------------------------------------------

const outlines = new WeakMap<SpriteImage, Map<string, HTMLCanvasElement>>();

/**
 * An outline `thickness` pixels wide in `color` round every opaque pixel of an
 * image, on a canvas that many pixels bigger on each side: draw it at
 * (x - t, y - t) under or over the sprite at (x, y), where t is the thickness
 * in native pixels. A sprite built on the finer grid (sizedDinoSprite) asks
 * for its density, so the ring stays one native pixel wide.
 *
 * A walk strip passes its `frame` width and gets each frame ringed on its
 * own, the frames laid out `frame + 2 * thickness` apart: ringed as one
 * picture, a tail near the edge of one frame drew a stray arc into the slice
 * of the next. Built once per image, colour, thickness and frame: keyed by the
 * image alone, the first colour asked for stuck, so a sprite ringed violet in
 * the yard came out violet in a gold ring elsewhere.
 */
export function outlineOf(
  img: SpriteImage,
  color: string,
  thickness = 1,
  frame = img.width,
): HTMLCanvasElement {
  const t = Math.max(1, Math.round(thickness));
  const cacheKey = `${color}/${t}/${frame}`;
  let byColor = outlines.get(img);
  const hit = byColor?.get(cacheKey);
  if (hit) return hit;
  const w = img.width;
  const h = img.height;
  const frames = Math.max(1, Math.round(w / frame));
  const FW = frame + 2 * t;
  const W = frames * FW;
  const H = h + 2 * t;
  const src = pixelsOf(img, 0, 0, w, h);
  const canvas = canvasOf(W, H);
  const out = new ImageData(W, H);
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  for (let k = 0; k < frames; k++) {
    // Grow the frame's opaque shape t times by its four neighbours; what it grows into is the ring.
    const solid = new Uint8Array(FW * H);
    for (let y = 0; y < h; y++)
      for (let x = 0; x < frame; x++)
        if ((src[(y * w + k * frame + x) * 4 + 3] ?? 0) > 0) solid[(y + t) * FW + x + t] = 1;
    let mask = solid;
    for (let i = 0; i < t; i++) {
      const grown = new Uint8Array(mask);
      for (let y = 0; y < H; y++)
        for (let x = 0; x < FW; x++) {
          if (mask[y * FW + x]) continue;
          if (
            (x > 0 && mask[y * FW + x - 1]) ||
            (x < FW - 1 && mask[y * FW + x + 1]) ||
            (y > 0 && mask[(y - 1) * FW + x]) ||
            (y < H - 1 && mask[(y + 1) * FW + x])
          )
            grown[y * FW + x] = 1;
        }
      mask = grown;
    }
    for (let y = 0; y < H; y++)
      for (let x = 0; x < FW; x++)
        if (mask[y * FW + x] && !solid[y * FW + x])
          out.data.set([r, g, b, 255], (y * W + k * FW + x) * 4);
  }
  canvas.getContext('2d')?.putImageData(out, 0, 0);
  if (!byColor) {
    byColor = new Map();
    outlines.set(img, byColor);
  }
  byColor.set(cacheKey, canvas);
  return canvas;
}
